/**
 * Offline Storage Manager
 * Handles caching of subscription status and offline queue for pending operations
 */

import type { Profile, PlanType, SubscriptionStatus } from '@/types/database';

// Keys for localStorage
const SUBSCRIPTION_CACHE_KEY = 'orcamigo_subscription_cache';
const OFFLINE_QUEUE_KEY = 'orcamigo_offline_queue';
const PROFILE_CACHE_KEY = 'orcamigo_profile_cache';
const OFFLINE_ID_MAP_KEY = 'orcamigo_offline_id_map';

// Cache duration: 30 days (generous to handle long offline periods)
const CACHE_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

// ===== SUBSCRIPTION/PROFILE CACHE =====

interface CachedSubscription {
  userId: string;
  planType: PlanType;
  subscriptionStatus: SubscriptionStatus;
  subscriptionEndDate: string | null;
  isPremium: boolean;
  lifetimeQuotesCreated: number;
  quotesCreatedInPeriod: number;
  quotesPeriodStart: string | null;
  bonusQuotes: number;
  bonusQuotesPeriodStart: string | null;
  cachedAt: number; // timestamp
}

interface CachedProfile {
  userId: string;
  profile: Profile;
  cachedAt: number;
}

/**
 * Cache the user's subscription status for offline access
 * GOLDEN RULE: User should remain PRO until server explicitly says otherwise
 */
export function cacheSubscriptionStatus(
  userId: string,
  profile: Profile
): void {
  if (!userId || !profile) return;

  const isPremium = 
    profile.is_premium === true ||
    profile.plan_type === 'pro' ||
    profile.plan_type === 'admin' ||
    profile.subscription_status === 'active' ||
    profile.subscription_status === 'trialing' ||
    (profile.subscription_end_date ? new Date(profile.subscription_end_date) > new Date() : false);

  const cached: CachedSubscription = {
    userId,
    planType: profile.plan_type ?? 'free',
    subscriptionStatus: profile.subscription_status ?? null,
    subscriptionEndDate: profile.subscription_end_date ?? null,
    isPremium,
    lifetimeQuotesCreated: profile.lifetime_quotes_created ?? 0,
    quotesCreatedInPeriod: profile.quotes_created_in_period ?? 0,
    quotesPeriodStart: profile.quotes_period_start ?? null,
    bonusQuotes: profile.bonus_quotes ?? 0,
    bonusQuotesPeriodStart: profile.bonus_quotes_period_start ?? null,
    cachedAt: Date.now(),
  };

  try {
    localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(cached));
  } catch (e) {
    console.error('[offlineStorage] Error caching subscription:', e);
  }
}

/**
 * Get cached subscription status (for offline/network error scenarios)
 */
export function getCachedSubscription(userId: string): CachedSubscription | null {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY);
    if (!cached) return null;

    const data: CachedSubscription = JSON.parse(cached);
    
    // Verify it's for the same user
    if (data.userId !== userId) return null;

    // Check if cache is still valid (30 days)
    if (Date.now() - data.cachedAt > CACHE_DURATION_MS) {
      localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
      return null;
    }

    return data;
  } catch (e) {
    console.error('[offlineStorage] Error reading cached subscription:', e);
    return null;
  }
}

/**
 * Cache full profile for offline access
 */
export function cacheProfile(userId: string, profile: Profile): void {
  if (!userId || !profile) return;

  const cached: CachedProfile = {
    userId,
    profile,
    cachedAt: Date.now(),
  };

  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cached));
    // Also cache subscription status
    cacheSubscriptionStatus(userId, profile);
  } catch (e) {
    console.error('[offlineStorage] Error caching profile:', e);
  }
}

/**
 * Get cached profile for offline access
 */
export function getCachedProfile(userId: string): Profile | null {
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) return null;

    const data: CachedProfile = JSON.parse(cached);
    
    if (data.userId !== userId) return null;

    if (Date.now() - data.cachedAt > CACHE_DURATION_MS) {
      localStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }

    return data.profile;
  } catch (e) {
    console.error('[offlineStorage] Error reading cached profile:', e);
    return null;
  }
}

/**
 * Clear all cached subscription data (on logout)
 */
export function clearSubscriptionCache(): void {
  try {
    localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
    localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch (e) {
    // Ignore
  }
}

// ===== OFFLINE QUEUE =====

export type OfflineOperationType =
  | 'create_quote'
  | 'create_client'
  | 'create_catalog_item'
  | 'update_quote'
  | 'delete_quote'
  | 'delete_client'
  | 'delete_catalog_item'
  | 'clear_data'
  | 'create_company'
  | 'update_profile'
  | 'delete_company'
  | 'create_finance_entry'
  | 'update_finance_entry'
  | 'delete_finance_entry'
  | 'create_service_order'
  | 'update_service_order'
  | 'delete_service_order'
  | 'create_appointment'
  | 'update_appointment'
  | 'delete_appointment'
  | 'create_contract'
  | 'update_contract'
  | 'delete_contract'
  | 'create_receipt'
  | 'update_receipt'
  | 'delete_receipt'
  | 'create_stock_movement';

export interface OfflineOperation {
  id: string;
  type: OfflineOperationType;
  data: any;
  createdAt: number;
  retries: number;
}

/**
 * Add an operation to the offline queue
 */
export function addToOfflineQueue(
  type: OfflineOperationType,
  data: any,
  operationId?: string,
): string {
  const operations = getOfflineQueue();

  if (operationId && operations.some((operation) => operation.id === operationId)) {
    return operationId;
  }
  
  const operation: OfflineOperation = {
    id: operationId || `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    data,
    createdAt: Date.now(),
    retries: 0,
  };

  operations.push(operation);

  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(operations));
  } catch (e) {
    console.error('[offlineStorage] Error adding to offline queue:', e);
    // If localStorage is full, try to remove oldest items
    if (operations.length > 1) {
      const reduced = operations.slice(-10); // Keep only last 10
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(reduced));
    }
  }

  return operation.id;
}

/**
 * Get all pending offline operations
 */
export function getOfflineQueue(): OfflineOperation[] {
  try {
    const queue = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (!queue) return [];
    return JSON.parse(queue);
  } catch (e) {
    console.error('[offlineStorage] Error reading offline queue:', e);
    return [];
  }
}

/**
 * Remove an operation from the queue (after successful sync)
 */
export function removeFromOfflineQueue(operationId: string): void {
  const operations = getOfflineQueue().filter((op) => op.id !== operationId);
  
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(operations));
  } catch (e) {
    console.error('[offlineStorage] Error removing from offline queue:', e);
  }
}

/**
 * Update retry count for an operation
 */
export function incrementOfflineOperationRetry(operationId: string): void {
  const operations = getOfflineQueue().map((op) => {
    if (op.id === operationId) {
      return { ...op, retries: op.retries + 1 };
    }
    return op;
  });
  
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(operations));
  } catch (e) {
    console.error('[offlineStorage] Error updating offline queue:', e);
  }
}

/**
 * Clear entire offline queue
 */
export function clearOfflineQueue(): void {
  try {
    localStorage.removeItem(OFFLINE_QUEUE_KEY);
  } catch (e) {
    // Ignore
  }
}

/**
 * Check if there are pending offline operations
 */
export function hasOfflineOperations(): boolean {
  return getOfflineQueue().length > 0;
}

// ===== OFFLINE ID MAP =====
// Maps temporary offline ids (offline_<ts>_<rnd>) to the real UUIDs assigned
// by Postgres after a create operation is synced. Used by NetworkContext to
// translate lingering references (client_id, product_id, quoteId, etc.) in
// update/delete ops that were queued before the create ran.

type OfflineIdMap = Record<string, string>;

function readOfflineIdMap(): OfflineIdMap {
  try {
    const raw = localStorage.getItem(OFFLINE_ID_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as OfflineIdMap) : {};
  } catch (e) {
    console.error('[offlineStorage] Error reading offline id map:', e);
    return {};
  }
}

function writeOfflineIdMap(map: OfflineIdMap): void {
  try {
    localStorage.setItem(OFFLINE_ID_MAP_KEY, JSON.stringify(map));
  } catch (e) {
    console.error('[offlineStorage] Error writing offline id map:', e);
  }
}

/**
 * O destino de um mapeamento tem que ser um UUID de verdade.
 *
 * POR QUE ISTO É ESSENCIAL: este módulo existe para garantir que NENHUM id
 * temporário chegue ao Postgres. Antes desta validação, só a CHAVE era checada —
 * o VALOR entrava como viesse. Um mapeamento `offline_a → offline_b` era
 * gravado sem reclamar e `resolveOfflineId` devolvia `'offline_b'`, que quem
 * chama manda direto para o banco: `invalid input syntax for type uuid`. A
 * operação falha, a FILA TRAVA, e todas as operações enfileiradas depois (de
 * qualquer módulo) param junto.
 *
 * Vale também contra um mapa forjado à mão no `localStorage`, que sem isto
 * redirecionaria um UPDATE/DELETE para o id que o atacante escolhesse.
 */
const REAL_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isRealId(value: unknown): value is string {
  return typeof value === 'string' && REAL_ID_PATTERN.test(value);
}

export function registerOfflineIdMapping(offlineId: string, realId: string): void {
  if (!offlineId || !realId) return;
  if (!offlineId.startsWith('offline_')) return;
  if (!isRealId(realId)) {
    // Nunca silencioso: se um create devolveu algo que não é UUID, o sync vai
    // quebrar adiante e alguém precisa conseguir descobrir por quê.
    console.error(
      '[offlineStorage] Mapeamento recusado: o id real não é um UUID.',
      { offlineId, realId },
    );
    return;
  }
  const map = readOfflineIdMap();
  map[offlineId] = realId;
  writeOfflineIdMap(map);
}

export function resolveOfflineId(offlineId: string): string | null {
  if (!offlineId || !offlineId.startsWith('offline_')) return null;
  const map = readOfflineIdMap();
  const resolved = map[offlineId];
  // Revalida na LEITURA, não só na escrita: o mapa mora no `localStorage` e pode
  // ter sido plantado por fora (ou gravado por uma versão anterior do app, que
  // não validava). Melhor devolver `null` — "ainda não sincronizado" — do que um
  // id que envenena a fila.
  return isRealId(resolved) ? resolved : null;
}

export function clearOfflineIdMap(): void {
  try {
    localStorage.removeItem(OFFLINE_ID_MAP_KEY);
  } catch {
    // Ignore
  }
}

// ===== PENDING-RECORD MUTATION HELPERS =====

/**
 * Fold an update op into its still-pending create op.
 * Returns a shallow-cloned create op with the update's payload merged in.
 * Currently only quote-shaped merges are meaningful; other create types have
 * no matching update op today, so we return the create unchanged.
 */
export function mergePendingUpdateIntoCreate(
  createOp: OfflineOperation,
  updatePayload: { updates?: any; items?: any[]; generalPhotos?: any[] }
): OfflineOperation {
  if (createOp.type === 'create_finance_entry') {
    // Fold the finance-entry update straight into the still-pending create so
    // the trailing update never has to reference an offline_ id.
    return {
      ...createOp,
      data: {
        ...createOp.data,
        entry: {
          ...(createOp.data?.entry ?? {}),
          ...(updatePayload?.updates ?? {}),
        },
      },
    };
  }

  if (createOp.type === 'create_appointment') {
    // Compromisso é plano (sem filhos): dobra a edição direto no create para o
    // trailing update nunca referenciar um id offline_ (mesmo padrão do finance).
    return {
      ...createOp,
      data: {
        ...createOp.data,
        appointment: {
          ...(createOp.data?.appointment ?? {}),
          ...(updatePayload?.updates ?? {}),
        },
      },
    };
  }

  if (createOp.type === 'create_contract') {
    // Contrato é plano (sem filhos): dobra a edição do RASCUNHO direto no create
    // para o trailing update nunca referenciar um id offline_ (mesmo padrão do
    // finance/compromisso). Enviar/assinar são online e nunca são enfileirados.
    return {
      ...createOp,
      data: {
        ...createOp.data,
        contract: {
          ...(createOp.data?.contract ?? {}),
          ...(updatePayload?.updates ?? {}),
        },
      },
    };
  }

  if (createOp.type === 'create_receipt') {
    // Recibo é plano (sem filhos): dobra a edição direto no create para o
    // trailing update nunca referenciar um id offline_ (mesmo padrão do
    // finance/compromisso/contrato).
    return {
      ...createOp,
      data: {
        ...createOp.data,
        receipt: {
          ...(createOp.data?.receipt ?? {}),
          ...(updatePayload?.updates ?? {}),
        },
      },
    };
  }

  if (createOp.type === 'create_service_order') {
    // Fold a service-order update into the still-pending create so the trailing
    // update never has to reference an offline_ id. Items/photos replace wholesale
    // when provided (mirrors the quote path below).
    return {
      ...createOp,
      data: {
        ...createOp.data,
        serviceOrder: {
          ...(createOp.data?.serviceOrder ?? {}),
          ...(updatePayload?.updates ?? {}),
        },
        items: updatePayload?.items ?? createOp.data?.items,
        photos: updatePayload?.generalPhotos ?? createOp.data?.photos,
      },
    };
  }

  if (createOp.type !== 'create_quote') return createOp;

  const nextQuote = {
    ...(createOp.data?.quote ?? {}),
    ...(updatePayload?.updates ?? {}),
  };

  return {
    ...createOp,
    data: {
      ...createOp.data,
      quote: nextQuote,
      items: updatePayload?.items ?? createOp.data?.items,
      generalPhotos: updatePayload?.generalPhotos ?? createOp.data?.generalPhotos,
    },
  };
}

/**
 * Remove every update/delete op in the queue that targets the given offline id.
 * Called when a create op is permanently dropped (MAX_RETRIES) so we don't
 * carry orphaned mutations that will fail with an offline_ UUID error.
 */
export function removeChainedOfflineOperations(offlineId: string): number {
  if (!offlineId) return 0;

  const queue = getOfflineQueue();
  const filtered = queue.filter((op) => {
    switch (op.type) {
      case 'update_quote':
        return op.data?.quoteId !== offlineId;
      case 'delete_quote':
        return op.data?.quoteId !== offlineId;
      case 'delete_client':
        return op.data?.clientId !== offlineId;
      case 'delete_catalog_item':
        return op.data?.itemId !== offlineId;
      case 'delete_company':
        return op.data?.companyId !== offlineId;
      case 'update_finance_entry':
        return op.data?.entryId !== offlineId;
      case 'delete_finance_entry':
        return op.data?.entryId !== offlineId;
      case 'update_service_order':
        return op.data?.serviceOrderId !== offlineId;
      case 'delete_service_order':
        return op.data?.serviceOrderId !== offlineId;
      case 'update_appointment':
        return op.data?.appointmentId !== offlineId;
      case 'delete_appointment':
        return op.data?.appointmentId !== offlineId;
      case 'update_contract':
        return op.data?.contractId !== offlineId;
      case 'delete_contract':
        return op.data?.contractId !== offlineId;
      case 'update_receipt':
        return op.data?.receiptId !== offlineId;
      case 'delete_receipt':
        return op.data?.receiptId !== offlineId;
      case 'create_stock_movement':
        // Movimento (append-only) referencia produto/orçamento/OS. Se o create
        // referenciado foi descartado, o movimento não tem como resolver — cai junto.
        return (
          op.data?.movement?.product_id !== offlineId &&
          op.data?.movement?.quote_id !== offlineId &&
          op.data?.movement?.service_order_id !== offlineId
        );
      default:
        return true;
    }
  });

  const removed = queue.length - filtered.length;
  if (removed > 0) {
    try {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(filtered));
    } catch (e) {
      console.error('[offlineStorage] Error removing chained ops:', e);
    }
  }
  return removed;
}

/**
 * When the user mutates (updates or deletes) a record whose id is still an
 * offline_<...> temp id, we must NOT enqueue a mutation carrying that temp id
 * — the create sync will produce a real UUID and the trailing update/delete
 * will explode with `invalid input syntax for type uuid: offline_...`.
 *
 * Instead:
 * - If a matching create op is still pending in the queue AND an update
 *   payload was provided: fold the update into the create.
 * - If a matching create op is still pending AND no update payload: this is a
 *   delete — drop the create op entirely (nothing to send).
 * - If no matching create op exists (already synced, then mapping should
 *   translate later, or was already dropped): remove any chained mutations
 *   and return removed=false so the caller can decide whether to fall back to
 *   a normal enqueue.
 *
 * Returns:
 *   removed:   true iff the pending record was handled inline (no new op
 *              should be enqueued by the caller)
 *   mergedInto: id of the create op the update was folded into, if any
 */
export function handleOfflineMutationOfPendingRecord(
  recordId: string,
  updatePayload?: { updates?: any; items?: any[]; generalPhotos?: any[] }
): { removed: boolean; mergedInto?: string } {
  if (!recordId || !String(recordId).startsWith('offline_')) {
    return { removed: false };
  }

  const queue = getOfflineQueue();
  const createOp = queue.find(
    (op) =>
      op.id === recordId &&
      (op.type === 'create_quote' ||
        op.type === 'create_client' ||
        op.type === 'create_catalog_item' ||
        op.type === 'create_company' ||
        op.type === 'create_finance_entry' ||
        op.type === 'create_service_order' ||
        op.type === 'create_appointment' ||
        op.type === 'create_contract' ||
        op.type === 'create_receipt')
  );

  if (!createOp) {
    // No pending create for this id. Clean any dangling chained mutations
    // (safety) and let the caller decide.
    removeChainedOfflineOperations(recordId);
    return { removed: false };
  }

  if (updatePayload) {
    // Fold the update into the pending create.
    const merged = mergePendingUpdateIntoCreate(createOp, updatePayload);
    const nextQueue = queue.map((op) => (op.id === createOp.id ? merged : op));
    try {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(nextQueue));
    } catch (e) {
      console.error('[offlineStorage] Error merging update into create:', e);
    }
    return { removed: true, mergedInto: createOp.id };
  }

  // Delete: drop the pending create and any chained mutations.
  const nextQueue = queue.filter((op) => op.id !== createOp.id);
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(nextQueue));
  } catch (e) {
    console.error('[offlineStorage] Error dropping pending create:', e);
  }
  removeChainedOfflineOperations(recordId);
  return { removed: true };
}
